import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import React, { useState, useRef, useEffect } from 'react';
import UploadComponent from './ui/UploadComponent';
import { List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { Check } from '@mui/icons-material';
import { Member } from '@/utils/types';
import MembersTable from './MembersTable';

interface ImportGroupProps {
    members: Member[];
    inviteMembers: (members: Member[]) => void
    error: boolean
    setoverlayComp: (val: boolean) => void
    setResultOverlayComp: (text: string | null) => void
    backToMembers: () => void
    licensesRemaining: number
}

export default function ImportGroup(props: ImportGroupProps) {

    const todos = ['First Name', 'Last Name', 'Email', 'Account type (student or advisor)', 'Graduation year (must be filled out for all students)', 'Department (can be left empty if not applicable)'];
    const [members, setMembers] = useState<Member[] | null>(null);
    const [errors, seterrors] = useState<string[]>([]);
    const [loading, setloading] = useState<boolean>(false);
    const [submitted, setSubmitted] = useState<boolean>(false);
    const REQUIRED_COLUMNS = ['first name', 'last name', 'email', 'account type', 'graduation year', 'department'];

    function createMember(
        firstname: string,
        lastname: string,
        email: string,
        year: number,
        membershipType: string,
        school: string
    ): Member {
        return {
            name: `${firstname} ${lastname}`,
            email,
            id: null,
            topPath: "",
            school,
            year,
            lastSession: "",
            membershipType
        };
    }

    const createIndexMap = (array: string[]): { [key: string]: number } => {
        return array.reduce((acc, item, index) => {
            acc[item.trim().toLowerCase()] = index;
            return acc;
        }, {} as { [key: string]: number });
    };

    const extractMembersFromExcel = (excel: (string)[][]) => {
        const headers = excel[0];
        const headerIndexMap = createIndexMap(headers);
        // Now you can easily access column positions
        const firstnameIndex = headerIndexMap['first name'];
        const lastnameIndex = headerIndexMap['last name'];
        const emailIndex = headerIndexMap['email'];
        const yearIndex = headerIndexMap['graduation year'];
        const memtypeIndex = headerIndexMap['account type'];
        const schoolIndex = headerIndexMap['department'];
        const newMems: Member[] = [];
        const errs = [];
        let licensesLeft = props.licensesRemaining;
        for (let i = 1; i < excel.length; i++) {
            const prop = excel[i];
            const missing: string[] = [];
            const fName = prop.at(firstnameIndex);
            const lName = prop.at(lastnameIndex);
            const email = prop.at(emailIndex);
            const memType = prop.at(memtypeIndex);
            const year = prop.at(yearIndex);
            const school = prop.at(schoolIndex);

            if (!email) {
                missing.push("email");
            }
            else if (props.members.some(x => x.email === email)) {
                errs.push(`${email} from row ${i}, has already been assigned a license.`)
                continue;
            }
            else if (newMems.some(x => x.email === email)) {
                errs.push(`${email} from row ${i}, has already been included earlier in the document.`)
                continue;
            }

            if (!fName) {
                missing.push("first name");
            }

            if (!lName) {
                missing.push("last name");
            }

            if (!memType) {
                missing.push("account type");
            }

            if (memType && memType == "student" && !year) {
                missing.push("graduation year");
            }

            if (missing.length === 0) {
                if (licensesLeft > 0) {
                    licensesLeft -= 1;
                    newMems.push(createMember(fName!, lName!, email?.toLowerCase()!, parseInt(year!) || 0, memType!, school || ""));
                }
                else{
                    errs.push(`${excel.length-i} member(s) were not imported as you have no more licenses left to assign.`);
                    break;
                }
            }
            else {
                errs.push(`Row ${i} is missing required values in the following columns: ${missing.join(',')}`)
            }
        }
        seterrors(errs);
        setMembers(newMems);
    }


    const handleSubmit = () => {
        if (members) {
            props.inviteMembers(members)
            setSubmitted(true);
            setloading(true);
        }
    };


    useEffect(() => {
        if (submitted) {
            props.setResultOverlayComp("successfully added new member(s)");
            setSubmitted(false);
            setTimeout(() => {
                props.setResultOverlayComp(null);
                props.backToMembers();
            }, 2000)
        }
    }, [props.members]);

    useEffect(() => {
        if (props.error && submitted) {
            setSubmitted(false);
            props.setResultOverlayComp("An error occured, please try again.")
            setTimeout(() => {
                props.setResultOverlayComp(null)
            }, 2000)
        }
    }, [props.error]);

    useEffect(() => {
        if (loading) {
            props.setoverlayComp(loading);
        }
    }, [loading]);

    return (
        <div className="w-[100%]">
            <Box sx={{ width: '100%', boxShadow: '1', borderBottom: '1px solid rgba(0, 0, 0, 0.2)' }}>
                <Toolbar
                    sx={[
                        {
                            backgroundColor: "white",
                            pl: { sm: 2 },
                            pr: { xs: 1, sm: 1 },
                        }
                    ]}
                >
                    {(
                        <Typography
                            sx={{ flex: '1 1 100%', fontWeight: '450', textAlign: 'left' }}
                            variant="h6"
                            id="tableTitle"
                            component="div"
                        >
                            Import Group
                        </Typography>
                    )}
                </Toolbar>
            </Box>
            <Box sx={{ width: '100%', boxShadow: '1', padding: '2rem', backgroundColor: "white" }}>
                <div className='flex mb-8  text-left w-[100%]'>
                    <p className='mr-5 font-bold'>New Member Info</p>
                    <p className='text-customBlack'>Add their info and we will send an invite for them to join. The new member license is active after they sign in for the first time.</p>
                </div>

                <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', marginBottom: 4, flexDirection: 'row', alignItems: 'flex-start' }}>
                        {/* Blue Circle with Number */}
                        <Box
                            sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                backgroundColor: '#2a2e5a',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: 2
                            }}
                        >
                            <Typography sx={{ color: 'white', fontWeight: 'bold' }}>1</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Typography sx={{ color: 'rgba(0, 0, 0, 0.6)', marginBottom: '0.5rem', fontWeight: 'bolder' }}>File types</Typography>
                            <Typography sx={{ color: 'rgba(0, 0, 0, 0.6)', }}>Add your group information to an Excel (.xlsx) file.</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', marginBottom: 4, alignItems: 'flex-start' }}>
                        {/* Blue Circle with Number */}
                        <Box
                            sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                backgroundColor: '#2a2e5a',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: 2
                            }}
                        >
                            <Typography sx={{ color: 'white', fontWeight: 'bold' }}>2</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Typography sx={{ color: 'rgba(0, 0, 0, 0.6)', marginBottom: '0.5rem', fontWeight: 'bolder' }}>Prepare your file for import</Typography>
                            <Typography sx={{ color: 'rgba(0, 0, 0, 0.6)', }}>Add <span className="font-bold">ALL</span> these field titles to the first row</Typography>

                            {/* To-Do List */}
                            <List>
                                {todos.map((todo, index) => (
                                    <ListItem key={index} sx={{ display: 'flex', paddingY: "0", paddingLeft: "0", alignItems: 'center', lineHeight: "0.25" }}>
                                        <ListItemIcon>
                                            <Check sx={{ color: '#2a2e5a' }} /> {/* Always show a checkmark */}
                                        </ListItemIcon>
                                        <ListItemText sx={{ lineHeight: "0.25", color: 'rgba(0, 0, 0, 0.6)' }} primary={todo} />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', marginBottom: 4, width: '100%', flexDirection: 'row', alignItems: 'flex-start' }}>
                        {/* Blue Circle with Number */}
                        <Box
                            sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                backgroundColor: '#2a2e5a',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: 2
                            }}
                        >
                            <Typography sx={{ color: 'white', fontWeight: 'bold' }}>3</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', alignItems: 'flex-start' }}>
                            <Typography sx={{ color: 'rgba(0, 0, 0, 0.6)', marginBottom: '0.5rem', fontWeight: 'bolder' }}>Upload your file</Typography>
                        </Box>
                    </Box>
                    <div className="w-[100%] flex justify-center">
                        <UploadComponent text="Drag your file or click to browse a file"
                            requiredColumns={REQUIRED_COLUMNS}
                            handleFunc={extractMembersFromExcel}
                            contentChange={(x) => { seterrors([]); setMembers(null) }} />
                    </div>
                    {members && <MembersTable
                        members={members}
                        isInviteTable={true} />}

                </Box>
                {errors.length > 0 && <div className="self-start text-left  text-red-500">
                    <p className="font-bold">Errors:</p>
                    {errors.map((x, i) => <p key={`${i}`}>{`${i + 1}. ${x}`}</p>)}
                </div>}
                {members && members.length > 0 && <div className='w-[unset] justify-self-center mt-4 px-3 py-2 text-white cursor-pointer hover:bg-grayBlue-hover font-[300] bg-grayBlue rounded-md shadow-sm' onClick={handleSubmit}><p>Send Invites</p></div>}

            </Box>
        </div>
    );
}